import { Popover, Transition } from '@headlessui/react'
import { ChatAlt2Icon } from '@heroicons/react/outline'
import { PaperAirplaneIcon, XCircleIcon } from '@heroicons/react/solid'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import ClickAwayListener from 'react-click-away-listener'
import { toast } from 'react-toastify'
import HomeApi from 'shared/api/home/Api'
import { UsersResponse } from 'shared/api/home/ApiGlobals'
import ManagerApi from 'shared/api/manager/Api'
import { Requester } from 'shared/api/manager/ApiGlobals'
import BaseCheckbox from 'shared/components/base/form/base-checkbox'
import MaskInput from 'shared/components/base/form/mask-input/MaskInput'
import LoadingSpinner from 'shared/components/loading/loading-spinner'
import { useLoggedInUser } from 'shared/contexts/useLoggedInUser'

import { Props, SmsOption, SmsTypeEnum } from './type'

const options: SmsOption[] = [
  { label: 'پیام معرفی من', value: SmsTypeEnum.INTRODUCE },
  { label: 'آدرس سایت آهن آنلاین', value: SmsTypeEnum.SITE },
  { label: 'اطلاعات سازمانی اکسین پولاد', value: SmsTypeEnum.OXIN_POLAD },
  { label: 'اطلاعات سازمانی نگین پولاد', value: SmsTypeEnum.NEGIN_POLAD },
]

const SMSTooltip = ({ defaultPhonNumber }: Props) => {
  const [smsData, setSmsData] = useState<{ type: SmsTypeEnum; number: string }>(
    { type: SmsTypeEnum.INTRODUCE, number: defaultPhonNumber || '' }
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [userDetail, setUserDetail] = useState<UsersResponse>()

  const { loggedInUser } = useLoggedInUser()

  const handleCheckBox = (type: SmsTypeEnum) =>
    setSmsData({ ...smsData, type: type })

  const fetchUserDetail = () => {
    if (loggedInUser?.id)
      HomeApi.users.usersDetail(loggedInUser?.id).then((res) => {
        setUserDetail(res.data)
      })
  }

  const cancel = () => {
    setSmsData({
      type: SmsTypeEnum.INTRODUCE,
      number: defaultPhonNumber || '',
    })
  }

  const submit = async () => {
    if (userDetail?.id && String(smsData.number).split('').length === 11) {
      setIsLoading(true)

      const requester: Requester = {
        mobilePhoneNumber: userDetail?.mobilePhoneNumber,
        id: userDetail?.id,
        extension: loggedInUser?.userPhone,
        domainName: loggedInUser?.userDomain,
        fullName: userDetail?.fullName,
        position: userDetail?.position,
        team: userDetail?.team,
        teamId: loggedInUser?.teamId,
      }
      let submitReq: any = null
      switch (smsData.type) {
        case SmsTypeEnum.INTRODUCE:
          submitReq = ManagerApi.sms.sendSellerInfoCreate
          break

        case SmsTypeEnum.SITE:
          submitReq = ManagerApi.sms.sendPriceListCreate
          break

        case SmsTypeEnum.OXIN_POLAD:
          submitReq = ManagerApi.sms.sendOxinInfoCreate
          break

        case SmsTypeEnum.NEGIN_POLAD:
          submitReq = ManagerApi.sms.sendNeginInfoCreate
          break
      }
      try {
        if (submitReq) {
          await submitReq(
            { phoneNumber: smsData?.number },
            { requester: requester }
          ).then(() => {
            toast.success('پیام شما با موفقیت ارسال شد')
          })
        }
      } catch (e) {
        toast.error('مشکل در ارتباط با سرور')
      } finally {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    fetchUserDetail()
  }, [])

  useEffect(() => {
    setSmsData({ ...smsData, number: defaultPhonNumber || '' })
  }, [defaultPhonNumber])

  return (
    <Popover className="relative">
      {({ close, open }) => {
        return (
          <>
            <Popover.Button>
              <ChatAlt2Icon
                className={classNames(
                  'w-7',
                  open ? 'text-blue-bullet' : 'text-[#8B8F9A]'
                )}
              />
            </Popover.Button>
            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-100 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <ClickAwayListener onClickAway={cancel}>
                <Popover.Panel className="absolute z-10 bg-gunmetal-blue pt-[17px] pb-[19px] px-4 left-12 top-[-50px] rounded-lg ">
                  {/*----------- header---------------*/}
                  <div className={'flex justify-center text-[#7E92A2]'}>
                    <XCircleIcon
                      className={'w-5 absolute cursor-pointer right-4 top-3'}
                      onClick={() => {
                        cancel()
                        close()
                      }}
                    />
                    <span className={' font-normal text-[12px] leading-7'}>
                      {' '}
                      ارسال پیامک (sms)
                    </span>
                  </div>
                  {/*-----------end header---------------*/}

                  {/*-------------- input------------*/}
                  <MaskInput
                    value={smsData.number}
                    mask={'0000 000 0000'}
                    className={
                      '  w-[209px] h-[35px] rounded-lg !text-[#3639A6] bg-red-300 '
                    }
                    style={{ direction: 'ltr' }}
                    textClassName={
                      'text-text-purple  font-medium text-[13px] leading-[22px] pt-[2px]'
                    }
                    inputClassName={
                      'pl-[50px] box-content bg-white mt-2 !text-[#3639A6] '
                    }
                    onAccept={(value: string, maskRef: any) => {
                      setSmsData({ ...smsData, number: maskRef?.unmaskedValue })
                    }}
                  />

                  {/*----- checkBoxes -----------*/}
                  <div className={'space-y-1 mt-[35px]'}>
                    {options?.map((node, index) => (
                      <BaseCheckbox
                        inputClassName={'!w-4 !h-4 leading-9'}
                        labelClassName={classNames(
                          'font-normal text-[12px] leading-9 text-white',
                          index != options.length - 1
                            ? 'border-b border-gray-600 '
                            : undefined
                        )}
                        onChange={() => handleCheckBox(node.value)}
                        key={index}
                        label={node.label}
                        value={node.value}
                        checked={node.value === smsData.type}
                      />
                    ))}
                  </div>

                  <div className={'w-full flex justify-end mt-[20px]'}>
                    <button
                      disabled={isLoading}
                      onClick={isLoading ? undefined : submit}
                      type="button"
                      className={
                        'bg-green-cta w-[40px] h-[40px] rounded-full p-3 flex items-center justify-center'
                      }
                    >
                      {isLoading ? (
                        <LoadingSpinner />
                      ) : (
                        <PaperAirplaneIcon
                          className={'text-white rotate-[-90deg]'}
                        />
                      )}
                    </button>
                  </div>
                </Popover.Panel>
              </ClickAwayListener>
            </Transition>
          </>
        )
      }}
    </Popover>
  )
}
export default SMSTooltip
